import { useEffect, useRef, useState } from 'react';

/**
 * Split a WebGL layer's visibility into two states: what the user asked for and
 * what is actually drawn.
 *
 * A WebGL vector layer with an empty source can paint a solid white framebuffer
 * over everything beneath it, so the layer has to be off whenever it has no
 * features. The aggregations these layers render are derived from the visible
 * detections, so hiding the detections empties them — the preference is what
 * puts the layer back on the map when they return, and what keeps the checkbox
 * ticked in the meantime.
 *
 * The layer is also toggled from outside React: `useAreaDraw` hides the
 * collection layers for the duration of an AOI sketch and afterward restores
 * exactly the ones that were visible. Those toggles are mirrored into the
 * preference. A hide with no features is this hook's own and is ignored, since
 * treating it as the user's would discard the preference.
 *
 * @param {import('ol/layer/Layer').default|null} layer
 * @param {boolean} hasFeatures - whether the layer's source has any features
 * @returns {{
 *   isEnabledByUser: boolean,
 *   setEnabledByUser: (enabled: boolean) => void,
 *   isOnMap: boolean,
 * }}
 */
const useLayerVisibilityPreference = (layer, hasFeatures) => {
	const [isEnabledByUser, setEnabledByUser] = useState(false);

	// Read by the change:visible listener, which fires after this render.
	const hasFeaturesRef = useRef(hasFeatures);
	hasFeaturesRef.current = hasFeatures;

	useEffect(() => {
		if (!layer) return;
		layer.setVisible(isEnabledByUser && hasFeatures);
	}, [layer, isEnabledByUser, hasFeatures]);

	useEffect(() => {
		if (!layer) return;
		const handleChange = () => {
			if (layer.getVisible()) setEnabledByUser(true);
			else if (hasFeaturesRef.current) setEnabledByUser(false);
		};
		layer.on('change:visible', handleChange);
		return () => layer.un('change:visible', handleChange);
	}, [layer]);

	return { isEnabledByUser, setEnabledByUser, isOnMap: isEnabledByUser && hasFeatures };
};

export default useLayerVisibilityPreference;
